import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  CircularProgress,
  Autocomplete,
  IconButton,
  Tooltip,
  useTheme,
  Divider,
  Typography,
  Chip,
  alpha,
  FormControlLabel,
  Switch,
} from '@mui/material';
import { 
  Refresh, 
  Close as CloseIcon,
  Category as CategoryIcon,
  AccountBalance as AccountIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { useLanguage } from '../../contexts/LanguageContext';
import { categoriesService } from '../../services/categories.service';
import { chartOfAccountsService, ChartOfAccountDto as ChartOfAccount } from '../../services/chartOfAccounts.service';
import { CreateCategoryDto, Category } from '../../types/categories.types';

const translations = {
  en: {
    addCategory: 'Add New Category',
    editCategory: 'Edit Category',
    basicInfo: 'Basic Information',
    accountingInfo: 'Accounting Integration',
    bakerySettings: 'Bakery Settings',
    categoryCode: 'Category Code',
    categoryName: 'Category Name (English)',
    categoryNameAr: 'Category Name (Arabic)',
    parentCategory: 'Parent Category',
    expenseAccount: 'Expense Account',
    noParent: 'Main Category (No Parent)',
    cancel: 'Cancel',
    save: 'Save',
    loading: 'Loading...',
    generateCode: 'Generate Code',
    categoryCreated: 'Category created successfully',
    categoryUpdated: 'Category updated successfully',
    failedToSave: 'Failed to save category',
    loadDataError: 'Failed to load data',
    optional: 'Optional',
    required: 'Required',
    isAllowedForBakeryIssue: 'Allowed for Bakery Sales (Issue to Branches)',
    isAllowedForBakeryIssueDesc: 'Finished goods — can be issued to branches from bakery',
    isAllowedForBakeryPurchase: 'Allowed for Bakery Purchases',
    isAllowedForBakeryPurchaseDesc: 'Raw materials — can be purchased for the bakery warehouse',
  },
  ar: {
    addCategory: 'إضافة تصنيف جديد',
    editCategory: 'تعديل تصنيف',
    basicInfo: 'المعلومات الأساسية',
    accountingInfo: 'الربط المحاسبي',
    bakerySettings: 'إعدادات المخبز',
    categoryCode: 'كود التصنيف',
    categoryName: 'اسم التصنيف (إنجليزي)',
    categoryNameAr: 'اسم التصنيف (عربي)',
    parentCategory: 'التصنيف الأب',
    expenseAccount: 'حساب المصروفات',
    noParent: 'تصنيف رئيسي (بدون أب)',
    cancel: 'إلغاء',
    save: 'حفظ',
    loading: 'جاري التحميل...',
    generateCode: 'توليد الكود',
    categoryCreated: 'تم إنشاء التصنيف بنجاح',
    categoryUpdated: 'تم تحديث التصنيف بنجاح',
    failedToSave: 'فشل حفظ التصنيف',
    loadDataError: 'فشل تحميل البيانات',
    optional: 'اختياري',
    required: 'مطلوب',
    isAllowedForBakeryIssue: 'مسموح في مبيعات المخبز (صرف للفروع)',
    isAllowedForBakeryIssueDesc: 'منتجات جاهزة — يمكن صرفها للفروع من المخبز',
    isAllowedForBakeryPurchase: 'مسموح في مشتريات المخبز',
    isAllowedForBakeryPurchaseDesc: 'مواد خام — يمكن شراؤها لمخزن المخبز',
  },
};

interface CategoryDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: () => void;
  editCategory?: Category | null;
  allCategories: Category[];
}

const CategoryDialog: React.FC<CategoryDialogProps> = ({ 
  open, 
  onClose, 
  onSave, 
  editCategory,
  allCategories 
}) => {
  const { language } = useLanguage();
  const t = translations[language];
  const isRTL = language === 'ar';
  const theme = useTheme();
  const isDarkMode = theme.palette.mode === 'dark';

  const [loading, setLoading] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [accounts, setAccounts] = useState<ChartOfAccount[]>([]);

  const [formData, setFormData] = useState<CreateCategoryDto & { expenseAccountId?: number; isAllowedForBakeryIssue: boolean; isAllowedForBakeryPurchase: boolean }>({
    categoryCode: '',
    categoryName: '',
    categoryNameAr: '',
    parentCategoryId: undefined,
    expenseAccountId: undefined,
    isAllowedForBakeryIssue: false,
    isAllowedForBakeryPurchase: false,
  });

  useEffect(() => {
    if (open) {
      loadAccounts();
      if (editCategory) {
        setFormData({
          categoryCode: editCategory.categoryCode,
          categoryName: editCategory.categoryName,
          categoryNameAr: editCategory.categoryNameAr,
          parentCategoryId: editCategory.parentCategoryId,
          expenseAccountId: (editCategory as any).expenseAccountId,
          isAllowedForBakeryIssue: editCategory.isAllowedForBakeryIssue ?? false,
          isAllowedForBakeryPurchase: (editCategory as any).isAllowedForBakeryPurchase ?? false,
        });
      } else {
        resetForm();
      }
    }
  }, [open, editCategory]);

  const loadAccounts = async () => {
    try {
      const data = await chartOfAccountsService.getAll();
      // فقط حسابات المصروفات
      const expenseAccounts = data.filter(acc => acc.accountType === 'Expense' && acc.isFinalAccount);
      setAccounts(expenseAccounts);
    } catch (err) {
      toast.error(t.loadDataError, { position: 'top-center', autoClose: 3000 });
    }
  };

  const handleGenerateCode = async () => {
    try {
      setGenerating(true);
      const newCode = await categoriesService.generateCode(formData.parentCategoryId);
      handleChange('categoryCode', newCode);
      toast.success(`${t.generateCode}: ${newCode}`, { 
        position: 'top-center', 
        autoClose: 2000 
      });
    } catch (err) {
      toast.error(t.loadDataError, { position: 'top-center', autoClose: 3000 });
    } finally {
      setGenerating(false);
    }
  };

  const handleChange = (field: keyof typeof formData, value: any) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async () => {
    if (!formData.categoryCode || !formData.categoryName || !formData.categoryNameAr) {
      toast.error(t.failedToSave, { position: 'top-center', autoClose: 3000 });
      return;
    }

    try {
      setLoading(true);
      if (editCategory) {
        await categoriesService.update(editCategory.categoryId, {
          categoryId: editCategory.categoryId,
          categoryName: formData.categoryName,
          categoryNameAr: formData.categoryNameAr,
          isActive: editCategory.isActive,
          isAllowedForBakeryIssue: formData.isAllowedForBakeryIssue,
          isAllowedForBakeryPurchase: formData.isAllowedForBakeryPurchase,
          expenseAccountId: formData.expenseAccountId,
        } as any);
        toast.success(t.categoryUpdated, { 
          position: 'top-center', 
          autoClose: 2000,
          style: { fontSize: '16px', fontWeight: 'bold' }
        });
      } else {
        await categoriesService.create(formData as any);
        toast.success(t.categoryCreated, { 
          position: 'top-center', 
          autoClose: 2000,
          style: { fontSize: '16px', fontWeight: 'bold' }
        });
      }
      onSave();
      handleClose();
    } catch (err: any) {
      let errorMessage = t.failedToSave;
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
      } else if (err.response?.data && typeof err.response.data === 'string') {
        errorMessage = err.response.data;
      }
      
      toast.error(errorMessage, { 
        position: 'top-center', 
        autoClose: 5000,
        style: { fontSize: '16px', fontWeight: 'bold' }
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const resetForm = () => {
    setFormData({
      categoryCode: '',
      categoryName: '',
      categoryNameAr: '',
      parentCategoryId: undefined,
      expenseAccountId: undefined,
      isAllowedForBakeryIssue: false,
      isAllowedForBakeryPurchase: false,
    });
  };

  const availableParents = editCategory
    ? allCategories.filter(c => c.categoryId !== editCategory.categoryId)
    : allCategories;

  const selectedParent = availableParents.find(c => c.categoryId === formData.parentCategoryId) || null;
  const selectedAccount = accounts.find(a => a.accountId === formData.expenseAccountId) || null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="md" 
      fullWidth
      dir={isRTL ? 'rtl' : 'ltr'}
      PaperProps={{
        sx: {
          borderRadius: 3,
          bgcolor: isDarkMode ? '#1a1a2e' : 'white',
        }
      }}
    >
      <DialogTitle sx={{ 
        background: isDarkMode 
          ? 'linear-gradient(135deg, #0f3460 0%, #16213e 100%)' 
          : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', 
        color: 'white', 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center', 
        py: 2,
        fontWeight: 'bold'
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CategoryIcon />
          {editCategory ? t.editCategory : t.addCategory}
        </Box>
        <IconButton onClick={handleClose} sx={{ color: 'white' }}>
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 3 }}>
        {/* Basic Information Section */}
        <Box sx={{ mb: 3 }}>
          <Typography 
            variant="h6" 
            fontWeight="bold" 
            sx={{ 
              mb: 2, 
              color: '#667eea',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <CategoryIcon fontSize="small" />
            {t.basicInfo}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
            {/* Category Code */}
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
              <TextField
                fullWidth
                label={
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {t.categoryCode}
                    <Chip 
                      label={t.required} 
                      size="small" 
                      color="error" 
                      sx={{ height: 18, fontSize: '0.7rem' }}
                    />
                  </Box>
                }
                value={formData.categoryCode}
                onChange={(e) => handleChange('categoryCode', e.target.value)}
                required
                disabled={!!editCategory}
                id="category-code"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                  }
                }}
              />
              {!editCategory && (
                <Tooltip title={t.generateCode}>
                  <IconButton
                    onClick={handleGenerateCode}
                    disabled={generating}
                    sx={{ 
                      border: '2px solid',
                      borderColor: 'primary.main',
                      borderRadius: 2,
                      width: 56,
                      height: 56,
                      background: isDarkMode 
                        ? alpha(theme.palette.primary.main, 0.1)
                        : alpha(theme.palette.primary.main, 0.05),
                      '&:hover': {
                        background: isDarkMode 
                          ? alpha(theme.palette.primary.main, 0.2)
                          : alpha(theme.palette.primary.main, 0.1),
                      }
                    }}
                  >
                    {generating ? (
                      <CircularProgress size={24} color="primary" />
                    ) : (
                      <Refresh color="primary" />
                    )}
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {/* Category Names */}
            <TextField
              fullWidth
              label={
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {t.categoryName}
                  <Chip 
                    label={t.required} 
                    size="small" 
                    color="error" 
                    sx={{ height: 18, fontSize: '0.7rem' }}
                  />
                </Box>
              }
              value={formData.categoryName}
              onChange={(e) => handleChange('categoryName', e.target.value)}
              required
              id="category-name-en"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            <TextField
              fullWidth
              label={
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                  {t.categoryNameAr}
                  <Chip 
                    label={t.required} 
                    size="small" 
                    color="error" 
                    sx={{ height: 18, fontSize: '0.7rem' }}
                  />
                </Box>
              }
              value={formData.categoryNameAr}
              onChange={(e) => handleChange('categoryNameAr', e.target.value)}
              required
              id="category-name-ar"
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />

            {/* Parent Category */}
            <Autocomplete
              options={availableParents}
              getOptionLabel={(option) => 
                `${option.categoryCode} - ${isRTL ? option.categoryNameAr : option.categoryName}`
              }
              value={selectedParent}
              onChange={(_, newValue) => handleChange('parentCategoryId', newValue?.categoryId)}
              renderInput={(params) => (
                <TextField 
                  {...params} 
                  label={
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {t.parentCategory}
                      <Chip 
                        label={t.optional} 
                        size="small" 
                        color="info" 
                        sx={{ height: 18, fontSize: '0.7rem' }}
                      />
                    </Box>
                  }
                  id="parent-category" 
                />
              )}
              isOptionEqualToValue={(option, value) => option.categoryId === value.categoryId}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                }
              }}
            />
          </Box>
        </Box>

        {/* Bakery Settings Section */}
        <Box sx={{ mb: 3 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{
              mb: 2,
              color: '#f57c00',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            🥐 {t.bakerySettings}
          </Typography>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {/* مبيعات — صرف للفروع */}
            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'success.light', borderRadius: 2, bgcolor: 'success.50' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isAllowedForBakeryIssue}
                    onChange={(e) => handleChange('isAllowedForBakeryIssue', e.target.checked)}
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight="bold" color="success.main">🟢 {t.isAllowedForBakeryIssue}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.isAllowedForBakeryIssueDesc}</Typography>
                  </Box>
                }
              />
            </Box>
            {/* مشتريات — مواد خام */}
            <Box sx={{ p: 1.5, border: '1px solid', borderColor: 'primary.light', borderRadius: 2, bgcolor: 'primary.50' }}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.isAllowedForBakeryPurchase}
                    onChange={(e) => handleChange('isAllowedForBakeryPurchase', e.target.checked)}
                    color="primary"
                  />
                }
                label={
                  <Box>
                    <Typography fontWeight="bold" color="primary.main">🔵 {t.isAllowedForBakeryPurchase}</Typography>
                    <Typography variant="caption" color="text.secondary">{t.isAllowedForBakeryPurchaseDesc}</Typography>
                  </Box>
                }
              />
            </Box>
          </Box>
        </Box>

        {/* Accounting Integration Section */}
        <Box>
          <Typography 
            variant="h6" 
            fontWeight="bold" 
            sx={{ 
              mb: 2, 
              color: '#667eea',
              display: 'flex',
              alignItems: 'center',
              gap: 1
            }}
          >
            <AccountIcon fontSize="small" />
            {t.accountingInfo}
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Autocomplete
            options={accounts}
            getOptionLabel={(option) => 
              `${option.accountCode} - ${isRTL ? option.accountNameAr : option.accountName}`
            }
            value={selectedAccount}
            onChange={(_, newValue) => handleChange('expenseAccountId', newValue?.accountId)}
            renderInput={(params) => (
              <TextField 
                {...params} 
                label={
                  <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    {t.expenseAccount}
                    <Chip 
                      label={t.optional} 
                      size="small" 
                      color="info" 
                      sx={{ height: 18, fontSize: '0.7rem' }}
                    />
                  </Box>
                }
              />
            )}
            isOptionEqualToValue={(option, value) => option.accountId === value.accountId}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              }
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ 
        p: 3, 
        bgcolor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f5f5f5',
        gap: 1
      }}>
        <Button 
          onClick={handleClose} 
          disabled={loading}
          size="large"
          sx={{
            borderRadius: 2,
            px: 3
          }}
        >
          {t.cancel}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={20} /> : null}
          size="large"
          sx={{
            borderRadius: 2,
            px: 4,
            background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
            boxShadow: '0 3px 5px 2px rgba(102, 126, 234, .3)',
            '&:hover': { 
              background: 'linear-gradient(45deg, #5568d3 30%, #653d8a 90%)',
              boxShadow: '0 5px 8px 3px rgba(102, 126, 234, .4)',
            }
          }}
        >
          {t.save}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default CategoryDialog;